import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import { useAuth } from "../context/AuthContext";
import "./ReportsSectionCompact.css";
import {
  getBreakEvenConfig,
  runBreakEvenReport,
  updateBreakEvenConfig,
} from "../api/breakEvenReport";

dayjs.extend(utc);
dayjs.extend(timezone);

const HELP_SECTIONS = [
  {
    id: "snapshot",
    title: "Snapshot",
    description: "See the break-even sales target, clients needed, and capacity status first.",
    anchorId: "be-snapshot",
  },
  {
    id: "drivers",
    title: "What Drives This Number?",
    description: "Review the few business drivers that most affect break-even.",
    anchorId: "be-drivers",
  },
  {
    id: "actions",
    title: "What Should I Do Next?",
    description: "Scan the highest-priority actions surfaced by the report.",
    anchorId: "be-actions",
  },
  {
    id: "capacity",
    title: "Capacity Check",
    description: "Compare clients needed with current team capacity and utilization required.",
    anchorId: "be-capacity",
  },
  {
    id: "advanced",
    title: "Advanced Analysis",
    description:
      "Open technical assumptions, source details, confidence evidence, sensitivity ranges, and scenario comparisons.",
    anchorId: "be-advanced",
  },
];

const INPUT_CLASS =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-200";

const CARD_CLASS = "rounded-2xl border border-slate-200 bg-white p-5";

const OVERRIDE_FIELDS = [
  {
    key: "avg_ticket_override",
    label: "Average sale per client override",
    measuredKey: "avg_ticket",
    sourceKey: "avg_ticket_source",
    tooltip: "If blank, the measured average sale per client is used when available.",
    formatter: "money",
  },
  {
    key: "clients_per_hour_per_tech_override",
    label: "Clients per hour per staff override",
    measuredKey: "clients_per_hour_per_tech",
    sourceKey: "clients_per_hour_per_tech_source",
    tooltip: "If blank, measured service speed is used when available.",
    formatter: "number",
  },
  {
    key: "hours_per_day_override",
    label: "Staffed hours per day override",
    measuredKey: "staffed_hours_per_day",
    sourceKey: "staffed_hours_per_day_source",
    tooltip: "If blank, the report uses scheduled staffed hours per open day.",
    formatter: "number",
  },
];

const FIXED_COST_FIELDS = [
  ["rent_monthly", "Rent (monthly)"],
  ["receptionist_monthly", "Receptionist (monthly)"],
  ["manager_monthly", "Manager (monthly)"],
  ["other_fixed_monthly", "Other fixed (monthly)"],
];

const RANGE_FIELDS = [
  ["avg_ticket_low", "Average sale low"],
  ["avg_ticket_high", "Average sale high"],
  ["clients_per_hour_low", "Clients/hour low"],
  ["clients_per_hour_high", "Clients/hour high"],
];

const confidenceBadgeClass = (label) => {
  if (label === "High") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (label === "Medium") return "bg-amber-100 text-amber-700 border-amber-200";
  return "bg-rose-100 text-rose-700 border-rose-200";
};

const sourceBadgeClass = (source) => {
  if (source === "measured") return "bg-emerald-100 text-emerald-700 border-emerald-200";
  if (source === "override") return "bg-sky-100 text-sky-700 border-sky-200";
  return "bg-slate-100 text-slate-700 border-slate-200";
};

const sourceLabel = (source) => {
  if (source === "measured") return "Measured";
  if (source === "override") return "Assumed (Override)";
  return "Assumed";
};

const ownerSourceLabel = (source) => {
  if (source === "measured") return "Using measured results from this date range";
  if (source === "override") return "Using your saved assumption";
  return "Using a fallback assumption";
};

const formatMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "Not enough data yet";
  const abs = Math.abs(num).toFixed(2);
  return num < 0 ? `-$${abs}` : `$${abs}`;
};

const formatNumber = (value, digits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "Not enough data yet";
  return num.toFixed(digits);
};

const formatPercent = (value, digits = 2) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return "Not enough data yet";
  return `${num.toFixed(digits)}%`;
};

const formatLabelList = (items) => {
  if (!items.length) return "";
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
};

const toOptionalNumber = (value) => {
  if (value === null || value === undefined || String(value).trim() === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const sumNumbers = (values) => {
  let hasFinite = false;
  const total = values.reduce((sum, value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return sum;
    hasFinite = true;
    return sum + num;
  }, 0);
  return hasFinite ? total : null;
};

const toCapacityStatus = ({ feasible, utilizationNeeded, capacityClientsPerDay, requiredClientsPerDay }) => {
  const capacityNum = Number(capacityClientsPerDay);
  const requiredNum = Number(requiredClientsPerDay);
  const utilizationNum = Number(utilizationNeeded);

  if (
    !Number.isFinite(capacityNum) ||
    capacityNum <= 0 ||
    !Number.isFinite(requiredNum) ||
    requiredNum <= 0
  ) {
    return {
      label: "Need More Inputs",
      detail: "Add staffed hours to estimate if the team can support this target.",
      badgeClass: "border-slate-200 bg-slate-50 text-slate-700",
      cardClass: "border-slate-200 bg-slate-50 text-slate-900",
    };
  }

  if (!feasible || utilizationNum > 1) {
    return {
      label: "Not Enough Capacity",
      detail: "The current team setup does not support this break-even target yet.",
      badgeClass: "border-rose-200 bg-rose-50 text-rose-700",
      cardClass: "border-rose-200 bg-rose-50 text-rose-900",
    };
  }

  if (utilizationNum >= 0.9) {
    return {
      label: "Tight",
      detail: "The team can support the target, but there is very little buffer.",
      badgeClass: "border-amber-200 bg-amber-50 text-amber-700",
      cardClass: "border-amber-200 bg-amber-50 text-amber-900",
    };
  }

  return {
    label: "On Track",
    detail: "The current team can support this break-even target with room to operate.",
    badgeClass: "border-emerald-200 bg-emerald-50 text-emerald-700",
    cardClass: "border-emerald-200 bg-emerald-50 text-emerald-900",
  };
};

const mapRecommendation = (item) => {
  const title = String(item?.title || "").toLowerCase();

  if (title.includes("capacity gap")) {
    return {
      title: "Increase team capacity on busy days",
      detail:
        "Your current demand target is above practical capacity. Focus on staffed hours, service speed, or coverage.",
    };
  }

  if (title.includes("average ticket")) {
    return {
      title: "Raise average sale per client",
      detail: "A stronger average sale lowers how many clients you need to break even.",
    };
  }

  if (title.includes("clients/hour")) {
    return {
      title: "Improve booking efficiency",
      detail: "Serving clients more efficiently increases how much demand your team can handle.",
    };
  }

  if (title.includes("fixed-cost")) {
    return {
      title: "Reduce fixed overhead",
      detail: "Lower overhead reduces the monthly sales target needed to break even.",
    };
  }

  if (title.includes("contribution margin")) {
    return {
      title: "Lower costs that increase with each sale",
      detail: "Reducing per-sale costs lets more revenue go toward fixed expenses.",
    };
  }

  return {
    title: item?.title || "Review projection assumptions",
    detail:
      item?.impact_hint ||
      "Review this recommendation and compare it with current operating targets.",
  };
};

export default function BreakEvenSensitivityReport() {
  const { token, currentSalon, user, salonTimezone: authSalonTimezone } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [config, setConfig] = useState(null);
  const [report, setReport] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saveMessage, setSaveMessage] = useState("");
  const [advancedAnalysisOpen, setAdvancedAnalysisOpen] = useState(false);
  const [scenarioView, setScenarioView] = useState("core");
  const [scenarioSearch, setScenarioSearch] = useState("");

  const salonId = useMemo(() => currentSalon?.id ?? user?.salon_id ?? null, [currentSalon, user]);
  const salonTz = useMemo(
    () =>
      currentSalon?.timezone ||
      currentSalon?.business?.timezone ||
      user?.salon_timezone ||
      authSalonTimezone ||
      "America/New_York",
    [currentSalon, user, authSalonTimezone]
  );

  useEffect(() => {
    if (!salonTz) return;
    const now = dayjs().tz(salonTz);
    const defaultEnd = now.format("YYYY-MM-DD");
    const defaultStart = now.subtract(12, "month").add(1, "day").format("YYYY-MM-DD");
    setEndDate((prev) => prev || defaultEnd);
    setStartDate((prev) => prev || defaultStart);
  }, [salonTz]);

  const loadConfig = useCallback(async () => {
    if (!token || !salonId) return;
    setLoadingConfig(true);
    setError("");
    try {
      const data = await getBreakEvenConfig(token, salonId);
      setConfig(data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load break-even assumptions.");
    } finally {
      setLoadingConfig(false);
    }
  }, [token, salonId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const runReport = useCallback(async () => {
    if (!token || !salonId || !startDate || !endDate) return;
    if (startDate > endDate) {
      setError("End date cannot be before start date.");
      return;
    }
    setRunning(true);
    setError("");
    try {
      const data = await runBreakEvenReport(
        {
          start_date: startDate,
          end_date: endDate,
          salon_id: salonId,
          mode: "range",
        },
        token
      );
      setReport(data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to run break-even projection.");
    } finally {
      setRunning(false);
    }
  }, [token, salonId, startDate, endDate]);

  useEffect(() => {
    if (config && startDate && endDate) {
      runReport();
    }
  }, [config, startDate, endDate, runReport]);

  useEffect(() => {
    if (!saveMessage) return;
    const timeout = setTimeout(() => setSaveMessage(""), 2000);
    return () => clearTimeout(timeout);
  }, [saveMessage]);

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...(prev || {}), [field]: value }));
    setSaveMessage("");
  };

  const handleSave = async () => {
    if (!token || !salonId || !config) return;
    setSaving(true);
    setError("");
    setSaveMessage("");
    try {
      const payload = {
        rent_monthly: Number(config.rent_monthly || 0),
        receptionist_monthly: Number(config.receptionist_monthly || 0),
        manager_monthly: Number(config.manager_monthly || 0),
        other_fixed_monthly: Number(config.other_fixed_monthly || 0),
        variable_cost_percent: Number(config.variable_cost_percent || 0),
        avg_ticket_override: toOptionalNumber(config.avg_ticket_override),
        clients_per_hour_per_tech_override: toOptionalNumber(
          config.clients_per_hour_per_tech_override
        ),
        hours_per_day_override: toOptionalNumber(config.hours_per_day_override),
        days_open_per_week: Math.min(
          7,
          Math.max(1, Math.floor(Number(config.days_open_per_week || 6)))
        ),
        active_tech_count_override: toOptionalNumber(config.active_tech_count_override),
        avg_ticket_low: toOptionalNumber(config.avg_ticket_low),
        avg_ticket_high: toOptionalNumber(config.avg_ticket_high),
        clients_per_hour_low: toOptionalNumber(config.clients_per_hour_low),
        clients_per_hour_high: toOptionalNumber(config.clients_per_hour_high),
        tech_count_low: toOptionalNumber(config.tech_count_low),
        tech_count_high: toOptionalNumber(config.tech_count_high),
        hours_per_day_low: toOptionalNumber(config.hours_per_day_low),
        hours_per_day_high: toOptionalNumber(config.hours_per_day_high),
        notes: config.notes || null,
      };
      const saved = await updateBreakEvenConfig(payload, token, salonId);
      setConfig(saved || payload);
      setSaveMessage("Saved");
      await runReport();
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to save assumptions.");
    } finally {
      setSaving(false);
    }
  };

  const measured = report?.measured_inputs || {};
  const assumptions = report?.assumptions_used || {};
  const breakEven = report?.break_even || {};
  const requiredVolume = report?.required_volume || {};
  const capacity = report?.capacity || {};
  const confidence = report?.confidence || {};
  const sensitivity = useMemo(() => report?.sensitivity || {}, [report]);
  const scenarios = useMemo(
    () => (Array.isArray(sensitivity?.scenarios) ? sensitivity.scenarios : []),
    [sensitivity]
  );
  const recommendations = Array.isArray(report?.recommendations) ? report.recommendations : [];

  const visibleScenarios = useMemo(() => {
    const search = scenarioSearch.trim().toLowerCase();
    return scenarios.filter((row) => {
      if (scenarioView === "core" && row.scenario_type !== "core") return false;
      if (!search) return true;
      return String(row.label || "").toLowerCase().includes(search);
    });
  }, [scenarios, scenarioSearch, scenarioView]);

  const confidenceScore = Number(confidence?.score || 0);
  const utilizationPct = Number(capacity?.utilization_needed) * 100;
  const dateRangeLabel = `${report?.date_range?.start_date || startDate} to ${
    report?.date_range?.end_date || endDate
  }`;
  const totalFixedMonthly = sumNumbers([
    assumptions.rent_monthly,
    assumptions.receptionist_monthly,
    assumptions.manager_monthly,
    assumptions.other_fixed_monthly,
  ]);
  const capacityStatus = toCapacityStatus({
    feasible: capacity?.feasible,
    utilizationNeeded: capacity?.utilization_needed,
    capacityClientsPerDay: capacity?.capacity_clients_per_day,
    requiredClientsPerDay: requiredVolume?.clients_per_day,
  });
  const ownerActions = recommendations
    .slice(0, 4)
    .map((item) => ({ ...item, ...mapRecommendation(item) }));
  const confidenceBreakdown = Array.isArray(confidence?.breakdown) ? confidence.breakdown : [];
  const ranges = sensitivity?.ranges || {};
  const capacityFormulaSummary = [
    Number.isFinite(Number(assumptions?.staffed_hours_per_day_final))
      ? `${formatNumber(assumptions.staffed_hours_per_day_final)} staffed hrs/day`
      : null,
    Number.isFinite(Number(assumptions?.clients_per_hour_per_tech_final))
      ? `${formatNumber(assumptions.clients_per_hour_per_tech_final)} clients/hr`
      : null,
  ]
    .filter(Boolean)
    .join(" x ");
  const utilizationExplanation =
    Number.isFinite(Number(utilizationPct)) &&
    Number.isFinite(Number(requiredVolume?.clients_per_day)) &&
    Number.isFinite(Number(capacity?.capacity_clients_per_day))
      ? `You need to use about ${formatPercent(
          utilizationPct
        )} of your scheduled client capacity to break even.`
      : "This shows how much of your scheduled client capacity you need to use to break even.";
  const utilizationMath =
    Number.isFinite(Number(utilizationPct)) &&
    Number.isFinite(Number(requiredVolume?.clients_per_day)) &&
    Number.isFinite(Number(capacity?.capacity_clients_per_day))
      ? `${formatNumber(requiredVolume.clients_per_day)} clients needed/day ÷ ${formatNumber(
          capacity.capacity_clients_per_day
        )} client capacity/day = ${formatPercent(utilizationPct)}`
      : null;
  const throughputExplanation =
    assumptions?.clients_per_hour_per_tech_source === "measured" &&
    Number.isFinite(Number(measured?.completed_appointments)) &&
    Number.isFinite(Number(measured?.completed_booked_hours)) &&
    Number(measured.completed_booked_hours) > 0 &&
    Number.isFinite(Number(assumptions?.clients_per_hour_per_tech_final))
      ? `Based on completed appointments and booked service hours in this date range. ${formatNumber(
          measured.completed_appointments,
          0
        )} completed appointments / ${formatNumber(
          measured.completed_booked_hours
        )} booked staff hrs = ${formatNumber(
          assumptions.clients_per_hour_per_tech_final
        )} clients/hr`
      : assumptions?.clients_per_hour_per_tech_source === "override"
      ? "Using your saved clients per hour assumption."
      : "Using the current clients per hour value for this report.";
  const payrollMonthly = sumNumbers([
    assumptions.receptionist_monthly,
    assumptions.manager_monthly,
  ]);
  const profitabilityMissingFields = [
    Number(assumptions.rent_monthly) > 0 ? null : "Rent (GIFI 8910 - Rental)",
    Number(payrollMonthly) > 0 ? null : "Payroll (GIFI 9060 - Salaries and wages)",
    Number(assumptions.other_fixed_monthly) > 0 ? null : "Other fixed monthly expenses",
  ].filter(Boolean);
  const needsProfitabilityInputs = profitabilityMissingFields.length > 0;

  return (
    <div className="reportSectionShell">
      <div className="reportSectionWide report-section-tight report-section-compact text-sm md:text-base">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title="Break-Even Point Sensitivity Analysis" />

          <div className="px-6 py-6 space-y-6">
            <ReportHelpCard
              title="Help Guide: Break-Even Point Sensitivity Analysis"
              subtitle="See the owner summary first, then expand advanced assumptions and scenario analysis when needed."
              sections={HELP_SECTIONS}
              onJumpToSection={jumpToSectionWithHighlight}
            />

            <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    Start date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    End date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={INPUT_CLASS}
                  />
                </div>
                <button
                  type="button"
                  onClick={runReport}
                  disabled={running || loadingConfig}
                  className="h-10 rounded-full bg-slate-900 px-5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                >
                  {running ? "Running..." : "Run Report"}
                </button>
              </div>
              <div className="text-xs text-slate-500">
                Date range: {dateRangeLabel} | Timezone: {report?.date_range?.tz_name || salonTz}
              </div>
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700">
                {error}
              </div>
            )}
            {saveMessage && (
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-emerald-700">
                {saveMessage}
              </div>
            )}

            <div
              id="be-snapshot"
              className={`${CARD_CLASS} space-y-4 ${getSectionClassName("be-snapshot")}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">Snapshot</h2>
                  <p className="mt-1 text-sm text-slate-600">
                    The fastest read on the sales target, client volume needed, and whether the
                    current team can support it.
                  </p>
                </div>
                <span
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${confidenceBadgeClass(
                    confidence?.label
                  )}`}
                >
                  {confidence?.label || "Low"} confidence
                </span>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Break-even Monthly
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(breakEven.monthly_revenue)}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Sales needed each month to cover fixed costs.
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Break-even Daily
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(breakEven.daily_revenue)}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Daily sales needed based on the current assumptions.
                  </p>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Clients Needed Per Day
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatNumber(requiredVolume.clients_per_day)}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Based on the current average sale per client.
                  </p>
                </div>
                <div className={`rounded-xl border p-4 ${capacityStatus.cardClass}`}>
                  <div className="text-xs uppercase tracking-wide text-current opacity-70">
                    Capacity Status
                  </div>
                  <div className="mt-2 text-2xl font-semibold">{capacityStatus.label}</div>
                  <p className="mt-2 text-xs opacity-80">{capacityStatus.detail}</p>
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Confidence score
                  </div>
                  <div className="mt-2 flex items-end justify-between gap-3">
                    <div className="text-2xl font-semibold text-slate-900">
                      {formatNumber(confidenceScore, 0)}
                    </div>
                    <div className="text-sm text-slate-500">
                      {confidence?.label || "Low"} confidence
                    </div>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-slate-100">
                    <div
                      className="h-2 rounded-full bg-rose-500"
                      style={{ width: `${Math.max(0, Math.min(100, confidenceScore))}%` }}
                    />
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Quick read</div>
                  <div className="mt-2 text-sm text-slate-700">
                    You need about{" "}
                    <span className="font-semibold text-slate-900">
                      {formatMoney(breakEven.daily_revenue)}
                    </span>{" "}
                    per day and{" "}
                    <span className="font-semibold text-slate-900">
                      {formatNumber(requiredVolume.clients_per_day)}
                    </span>{" "}
                    clients per day to break even under the current setup.
                  </div>
                  <div className="mt-2 text-xs text-slate-500">
                    This is a planning estimate for business decisions, not a guarantee.
                  </div>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                <h3 className="text-base md:text-lg font-semibold text-slate-900">
                  Detailed Targets
                </h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                      Revenue targets
                      <InfoTooltip
                        text="Break-even revenue = fixed cost / contribution margin."
                        label="Revenue targets formula"
                      />
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-slate-700">
                      <div>
                        Daily:{" "}
                        <span className="font-semibold text-slate-900">
                          {formatMoney(breakEven.daily_revenue)}
                        </span>
                      </div>
                      <div>
                        Weekly:{" "}
                        <span className="font-semibold text-slate-900">
                          {formatMoney(breakEven.weekly_revenue)}
                        </span>
                      </div>
                      <div>
                        Monthly:{" "}
                        <span className="font-semibold text-slate-900">
                          {formatMoney(breakEven.monthly_revenue)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                      Clients needed
                      <InfoTooltip
                        text="Required clients = break-even revenue / average sale per client."
                        label="Required clients formula"
                      />
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-slate-700">
                      <div>
                        Per day:{" "}
                        <span className="font-semibold text-slate-900">
                          {formatNumber(requiredVolume.clients_per_day)}
                        </span>
                      </div>
                      <div>
                        Per week:{" "}
                        <span className="font-semibold text-slate-900">
                          {formatNumber(requiredVolume.clients_per_week)}
                        </span>
                      </div>
                      <div>
                        Per month:{" "}
                        <span className="font-semibold text-slate-900">
                          {formatNumber(requiredVolume.clients_per_month)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4">
                    <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                      Clients per staff/day
                      <InfoTooltip
                        text="Required clients per day divided by active service staff."
                        label="Clients per staff/day"
                      />
                    </div>
                    <div className="mt-2 text-2xl font-semibold text-slate-900">
                      {formatNumber(requiredVolume.clients_per_tech_per_day)}
                    </div>
                    <div className="mt-2 text-xs text-slate-500">
                      Contribution margin: {formatPercent(breakEven.contribution_margin_pct)}
                    </div>
                  </div>
                </div>
              </div>
              {needsProfitabilityInputs && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">
                  <div className="font-semibold">Add the missing fixed-cost inputs in Profitability Estimate.</div>
                  <div className="mt-1 text-amber-800">
                    Enter {formatLabelList(profitabilityMissingFields)} in the Profitability
                    Estimate report, then rerun break-even for a more complete target.
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => navigate("/manager/reports/profitability-estimate")}
                      className="rounded-full border border-amber-300 bg-white px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                    >
                      Open Profitability Estimate
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div
              id="be-drivers"
              className={`${CARD_CLASS} space-y-4 ${getSectionClassName("be-drivers")}`}
            >
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  What Drives This Number?
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  These are the main levers shaping your break-even point right now.
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Monthly fixed costs
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(totalFixedMonthly)}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    Rent, front desk, manager payroll, and other fixed monthly costs.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Average sale per client
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatMoney(assumptions.avg_ticket_final)}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    {ownerSourceLabel(assumptions.avg_ticket_source)}
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                    Costs that increase with each sale
                    <InfoTooltip
                      text="Includes supplies, merchant fees, and other revenue-linked variable costs."
                      label="Costs that increase with each sale"
                    />
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatPercent(assumptions.variable_cost_percent)}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    These costs reduce how much of each sale goes toward fixed expenses.
                  </p>
                </div>

                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Current team capacity per day
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatNumber(capacity.capacity_clients_per_day)}
                  </div>
                  <p className="mt-2 text-xs text-slate-500">
                    {capacityFormulaSummary ||
                      "Based on scheduled service staff hours and service speed."}
                  </p>
                  <p className="mt-2 text-xs text-slate-500">{throughputExplanation}</p>
                </div>
              </div>
            </div>

            <div
              id="be-actions"
              className={`${CARD_CLASS} space-y-4 ${getSectionClassName("be-actions")}`}
            >
              <div>
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                  What Should I Do Next?
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Prioritized actions based on the current projection.
                </p>
              </div>

              {ownerActions.length === 0 ? (
                <div className="rounded-xl border border-slate-200 p-4 text-slate-600">
                  No urgent action signals yet. Use the advanced analysis section to review the full
                  assumptions and scenario detail.
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {ownerActions.map((item, idx) => (
                    <div
                      key={`${item.title}-${idx}`}
                      className="rounded-xl border border-slate-200 p-4 space-y-2"
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">
                          {idx + 1}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900">{item.title}</div>
                          <div className="mt-1 text-sm text-slate-600">{item.detail}</div>
                        </div>
                      </div>
                      <div className="text-xs text-slate-600">
                        <span className="rounded-full bg-slate-100 px-2 py-1">
                          Current: {item.current || "N/A"}
                        </span>
                        <span className="ml-2 rounded-full bg-rose-50 px-2 py-1 text-rose-700">
                          Target: {item.target || "N/A"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              id="be-capacity"
              className={`${CARD_CLASS} space-y-4 ${getSectionClassName("be-capacity")}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Capacity Check
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Compare the client volume needed with what the current team can realistically
                    support.
                  </p>
                </div>
                <span
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${capacityStatus.badgeClass}`}
                >
                  {capacityStatus.label}
                </span>
              </div>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Clients needed per day
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatNumber(requiredVolume.clients_per_day)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Current client capacity per day
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatNumber(capacity.capacity_clients_per_day)}
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-4">
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    Capacity Needed To Break Even
                  </div>
                  <div className="mt-2 text-2xl font-semibold text-slate-900">
                    {formatPercent(utilizationPct)}
                  </div>
                  <p className="mt-2 text-xs text-slate-600">{utilizationExplanation}</p>
                  {utilizationMath && (
                    <p className="mt-2 text-xs text-slate-500">{utilizationMath}</p>
                  )}
                </div>
                <div className={`rounded-xl border p-4 ${capacityStatus.cardClass}`}>
                  <div className="text-xs uppercase tracking-wide text-current opacity-70">
                    Status
                  </div>
                  <div className="mt-2 text-2xl font-semibold">{capacityStatus.label}</div>
                  <p className="mt-2 text-xs opacity-80">{capacityStatus.detail}</p>
                </div>
              </div>
              {capacity?.feasible === false && (
                <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-800 text-sm space-y-1">
                  <div className="font-semibold">
                    The current setup is below the projected demand target.
                  </div>
                  {Number(capacity.capacity_clients_per_day) <= 0 && (
                    <div>
                      Capacity is currently zero. Add staffed hours in Advanced analysis to run a
                      full capacity check.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div
              id="be-advanced"
              className={`${CARD_CLASS} space-y-4 ${getSectionClassName("be-advanced")}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg md:text-xl font-semibold text-slate-900">
                    Advanced analysis
                  </h2>
                  <p className="mt-1 text-sm text-slate-600">
                    Open the technical assumptions, confidence evidence, and full scenario table.
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    Scenario calculations use the underlying measured values before display
                    rounding, so table results may differ slightly from the rounded summary numbers
                    shown above.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setAdvancedAnalysisOpen((prev) => !prev)}
                  className="rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  {advancedAnalysisOpen ? "Hide advanced analysis" : "Show advanced analysis"}
                </button>
              </div>

              {advancedAnalysisOpen && (
                <div className="space-y-6 pt-1">
                  <div
                    id="be-inputs"
                    className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName(
                      "be-inputs"
                    )}`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <h3 className="text-base md:text-lg font-semibold text-slate-900">
                          Inputs & Assumptions
                        </h3>
                        <p className="mt-1 text-sm text-slate-600">
                          Edit saved planning assumptions and compare them with measured values.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving || loadingConfig || !config}
                        className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                    </div>

                    {loadingConfig || !config ? (
                      <div className="text-slate-500">Loading assumptions...</div>
                    ) : (
                      <>
                        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                          {OVERRIDE_FIELDS.map((field) => (
                            <div key={field.key} className="rounded-xl border border-slate-200 p-3">
                              <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                                {field.label}
                                <InfoTooltip text={field.tooltip} label={field.label} />
                              </div>
                              <input
                                type="number"
                                className={`${INPUT_CLASS} mt-2`}
                                value={config[field.key] ?? ""}
                                onChange={(e) => updateField(field.key, e.target.value)}
                              />
                              <div className="mt-2 text-xs text-slate-500">
                                Measured:{" "}
                                {field.formatter === "money"
                                  ? formatMoney(measured[field.measuredKey])
                                  : formatNumber(measured[field.measuredKey])}
                              </div>
                              <span
                                className={`mt-2 inline-flex rounded-full border px-2 py-0.5 text-[11px] font-semibold ${sourceBadgeClass(
                                  assumptions[field.sourceKey]
                                )}`}
                              >
                                {sourceLabel(assumptions[field.sourceKey])}
                              </span>
                            </div>
                          ))}

                          <div className="rounded-xl border border-slate-200 p-3">
                            <div className="text-xs uppercase tracking-wide text-slate-500">
                              Days open per week
                            </div>
                            <input
                              type="number"
                              min="1"
                              max="7"
                              className={`${INPUT_CLASS} mt-2`}
                              value={config.days_open_per_week ?? 6}
                              onChange={(e) => updateField("days_open_per_week", e.target.value)}
                            />
                          </div>

                          <div className="rounded-xl border border-slate-200 p-3">
                            <div className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-2">
                              Costs that increase with each sale
                              <InfoTooltip
                                text="Includes supplies, merchant fees, or other revenue-linked variable costs."
                                label="Costs that increase with each sale"
                              />
                            </div>
                            <input
                              type="number"
                              className={`${INPUT_CLASS} mt-2`}
                              value={config.variable_cost_percent ?? 0}
                              onChange={(e) => updateField("variable_cost_percent", e.target.value)}
                            />
                          </div>

                          {FIXED_COST_FIELDS.map(([key, label]) => (
                            <div key={key} className="rounded-xl border border-slate-200 p-3">
                              <div className="text-xs uppercase tracking-wide text-slate-500">
                                {label}
                              </div>
                              <input
                                type="number"
                                className={`${INPUT_CLASS} mt-2`}
                                value={config[key] ?? 0}
                                onChange={(e) => updateField(key, e.target.value)}
                              />
                            </div>
                          ))}
                        </div>

                        <div className="rounded-xl border border-slate-200 p-4 space-y-4">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              Sensitivity ranges
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              These low and high values are used for scenario analysis only.
                            </div>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
                            {RANGE_FIELDS.map(([key, label]) => (
                              <label key={key} className="text-sm text-slate-700">
                                {label}
                                <input
                                  type="number"
                                  className={`${INPUT_CLASS} mt-1`}
                                  value={config[key] ?? ""}
                                  onChange={(e) => updateField(key, e.target.value)}
                                />
                              </label>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="grid gap-6 lg:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
                      <div>
                        <h3 className="text-base md:text-lg font-semibold text-slate-900">
                          Confidence details
                        </h3>
                        <p className="mt-1 text-sm text-slate-600">
                          Evidence behind the report confidence score.
                        </p>
                      </div>
                      <div className="rounded-xl border border-slate-200 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-semibold text-slate-900">
                            Overall confidence
                          </div>
                          <span
                            className={`rounded-full border px-3 py-1 text-xs font-semibold ${confidenceBadgeClass(
                              confidence?.label
                            )}`}
                          >
                            {confidence?.label || "Low"}
                          </span>
                        </div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">
                          {formatNumber(confidenceScore, 0)}
                        </div>
                      </div>
                      <div className="space-y-3">
                        {confidenceBreakdown.length === 0 && (
                          <div className="rounded-xl border border-slate-200 p-4 text-slate-500">
                            No confidence breakdown available yet.
                          </div>
                        )}
                        {confidenceBreakdown.map((item) => (
                          <div key={item.component} className="rounded-xl border border-slate-200 p-4">
                            <div className="flex items-center justify-between gap-3">
                              <div className="font-semibold text-slate-900">{item.component}</div>
                              <div className="text-sm text-slate-500">
                                {item.points}/{item.max_points}
                              </div>
                            </div>
                            <div className="mt-2 text-sm text-slate-600">{item.detail}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div
                      id="be-sensitivity"
                      className={`rounded-2xl border border-slate-200 bg-white p-5 space-y-4 ${getSectionClassName(
                        "be-sensitivity"
                      )}`}
                    >
                      <div>
                        <h3 className="text-base md:text-lg font-semibold text-slate-900">
                          What happens if things change?
                        </h3>
                        <p className="mt-1 text-sm text-slate-600">
                          Range summaries and scenario comparisons for the main planning drivers.
                        </p>
                        <p className="mt-2 text-sm text-slate-500">
                          Base-case capacity uses{" "}
                          <span className="font-semibold text-slate-700">
                            {formatNumber(assumptions?.staffed_hours_per_day_final)} staffed hrs/day
                          </span>{" "}
                          from employee schedules or your staffed-hours override.
                        </p>
                      </div>

                      <div className="grid gap-3 md:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 p-4">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Average sale per client
                          </div>
                          <div className="mt-2 text-sm text-slate-700">
                            Low:{" "}
                            <span className="font-semibold text-slate-900">
                              {formatMoney(ranges?.avg_ticket?.low)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-700">
                            Base:{" "}
                            <span className="font-semibold text-slate-900">
                              {formatMoney(ranges?.avg_ticket?.base)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-700">
                            High:{" "}
                            <span className="font-semibold text-slate-900">
                              {formatMoney(ranges?.avg_ticket?.high)}
                            </span>
                          </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 p-4">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Clients per hour per staff
                          </div>
                          <div className="mt-2 text-sm text-slate-700">
                            Low:{" "}
                            <span className="font-semibold text-slate-900">
                              {formatNumber(ranges?.clients_per_hour?.low)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-700">
                            Base:{" "}
                            <span className="font-semibold text-slate-900">
                              {formatNumber(ranges?.clients_per_hour?.base)}
                            </span>
                          </div>
                          <div className="text-sm text-slate-700">
                            High:{" "}
                            <span className="font-semibold text-slate-900">
                              {formatNumber(ranges?.clients_per_hour?.high)}
                            </span>
                          </div>
                        </div>

                        <div className="rounded-xl border border-slate-200 p-4">
                          <div className="text-xs uppercase tracking-wide text-slate-500">
                            Staffed hours per day
                          </div>
                          <div className="mt-2 text-sm text-slate-700">
                            Low:{" "}
                            <span className="font-semibold text-slate-900">
                              {formatNumber(ranges?.staffed_hours_per_day?.low)} hrs/day
                            </span>
                          </div>
                          <div className="text-sm text-slate-700">
                            Base:{" "}
                            <span className="font-semibold text-slate-900">
                              {formatNumber(ranges?.staffed_hours_per_day?.base)} hrs/day
                            </span>
                          </div>
                          <div className="text-sm text-slate-700">
                            High:{" "}
                            <span className="font-semibold text-slate-900">
                              {formatNumber(ranges?.staffed_hours_per_day?.high)} hrs/day
                            </span>
                          </div>
                          <div className="mt-2 text-xs text-slate-500">
                            This is the capacity-hours input used directly in the report.
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-slate-900">
                          Scenario comparison
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <label className="text-xs text-slate-600">
                            Show
                            <select
                              className={`${INPUT_CLASS} ml-2 h-9 text-xs`}
                              value={scenarioView}
                              onChange={(e) => setScenarioView(e.target.value)}
                            >
                              <option value="core">Core scenarios</option>
                              <option value="all">All scenarios</option>
                            </select>
                          </label>
                          <input
                            type="text"
                            value={scenarioSearch}
                            onChange={(e) => setScenarioSearch(e.target.value)}
                            placeholder="Search label"
                            className="h-9 rounded-lg border border-slate-200 px-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-200"
                          />
                        </div>
                      </div>

                      <p className="text-xs text-slate-500">
                        Core scenarios keep the same staffed-hours-per-day basis as the summary
                        above. Staffed-hours scenarios show lower and higher capacity-hour cases.
                      </p>

                      <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                        <table className="w-full min-w-[980px] text-sm">
                          <thead className="bg-slate-50 text-slate-500 text-xs font-semibold uppercase tracking-wide">
                            <tr>
                              <th className="px-3 py-2 text-left">Scenario</th>
                              <th className="px-3 py-2 text-right">Average sale</th>
                              <th className="px-3 py-2 text-right">Clients/hr/staff</th>
                              <th className="px-3 py-2 text-right">Staffed hrs/day used</th>
                              <th className="px-3 py-2 text-right">Daily sales needed</th>
                              <th className="px-3 py-2 text-right">Clients needed/day</th>
                              <th className="px-3 py-2 text-right">Utilization needed</th>
                              <th className="px-3 py-2 text-right">Can support target</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {running && (
                              <tr>
                                <td className="px-3 py-4 text-slate-500" colSpan={8}>
                                  Running projection...
                                </td>
                              </tr>
                            )}
                            {!running && visibleScenarios.length === 0 && (
                              <tr>
                                <td className="px-3 py-4 text-slate-500" colSpan={8}>
                                  No scenarios found.
                                </td>
                              </tr>
                            )}
                            {!running &&
                              visibleScenarios.map((row) => (
                                <tr
                                  key={`${row.label}-${row.scenario_type}`}
                                  className="hover:bg-slate-50"
                                >
                                  <td className="px-3 py-2.5 font-semibold text-slate-900">
                                    {row.label}
                                  </td>
                                  <td className="px-3 py-2.5 text-right text-slate-700">
                                    {formatMoney(row.avg_ticket)}
                                  </td>
                                  <td className="px-3 py-2.5 text-right text-slate-700">
                                    {formatNumber(row.clients_per_hour)}
                                  </td>
                                  <td className="px-3 py-2.5 text-right text-slate-700">
                                    {formatNumber(row.staffed_hours_per_day)}
                                  </td>
                                  <td className="px-3 py-2.5 text-right text-slate-700">
                                    {formatMoney(row.break_even_daily_revenue)}
                                  </td>
                                  <td className="px-3 py-2.5 text-right text-slate-700">
                                    {formatNumber(row.required_clients_per_day)}
                                  </td>
                                  <td className="px-3 py-2.5 text-right text-slate-700">
                                    {Number.isFinite(Number(row.utilization_needed))
                                      ? `${(Number(row.utilization_needed) * 100).toFixed(2)}%`
                                      : "Not enough data yet"}
                                  </td>
                                  <td className="px-3 py-2.5 text-right">
                                    <span
                                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${
                                        row.feasibility_flag
                                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                          : "border-rose-200 bg-rose-50 text-rose-700"
                                      }`}
                                    >
                                      {row.feasibility_flag ? "Yes" : "No"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
